/* Tour "categorization-overview" — découverte de la catégorisation :
   1. Sur /categories : présenter la page, créer une catégorie d'exemple
      (l'utilisateur clique « + Catégorie », la modale est préremplie, il valide),
      puis expliquer la ligne créée (partie gauche : nom / TVA / type ; partie
      droite : dupliquer / déplacer / cacher / supprimer).
   2. Sur /transactions : (à finaliser) créer une transaction d'exemple, montrer
      le libellé, faire surligner un mot, créer une règle et l'affecter à la
      catégorie créée.

   La catégorie d'exemple est une vraie catégorie (créée par l'utilisateur pendant
   le guide) ; elle est supprimée à la sortie via le store pour rester robuste
   même hors de la page Catégories.

   NOTE : la partie /transactions est encore en cours (cf. TODO plus bas) — le
   guide s'arrête proprement après l'explication de la ligne créée. */

import store from '@/store';
import router from '@/router/router';
import AppBus from '@/bus/app-bus';
import './types';

// Nom de la catégorie d'exemple. Distinct de « Exemple Okimia » (forecast-objectives)
// pour éviter toute collision entre les deux sandbox.
const SEED_NAME = 'Catégorie Okimia';

// La ligne de la catégorie créée est marquée par Categories.vue
// (markTourCategory) via l'attribut data-tour-row="cat-demo", et son id mémorisé
// dans localStorage['fygr-tour-category'] pour le nettoyage.

// ── Rectangles de surbrillance gauche / droite ───────────────────────────────
// La ligne n'a pas de conteneur « gauche » ou « droite » distinct : on construit
// un repère fixe englobant les éléments visés.
function buildSpan(id: string, from: Element | null, to: Element | null): string | null {
  if (!from || !to) return null;
  const a = from.getBoundingClientRect();
  const b = to.getBoundingClientRect();
  const span = document.getElementById(id) || document.createElement('div');
  span.id = id;
  span.style.cssText = [
    'position:fixed',
    `top:${Math.min(a.top, b.top)}px`,
    `left:${Math.min(a.left, b.left)}px`,
    `width:${Math.max(a.right, b.right) - Math.min(a.left, b.left)}px`,
    `height:${Math.max(a.bottom, b.bottom) - Math.min(a.top, b.top)}px`,
    'pointer-events:none',
    'z-index:1',
  ].join(';');
  if (!span.parentNode) document.body.appendChild(span);
  return '#' + id;
}

function leftPartSelector(): string | null {
  const row = document.querySelector('[data-tour-row="cat-demo"]');
  if (!row) return null;
  return buildSpan(
    'tour-cat-left-span',
    row.querySelector('.fy-rc-line-name'),
    row.querySelector('.cat-type-dd'),
  );
}

function rightPartSelector(): string | null {
  const row = document.querySelector('[data-tour-row="cat-demo"]');
  if (!row) return null;
  return buildSpan(
    'tour-cat-right-span',
    row.querySelector('.fy-rc-line-move'),
    row.querySelector('.fy-rc-line-delete'),
  );
}

function cleanupSpans() {
  ['tour-cat-left-span', 'tour-cat-right-span', 'tour-tx-group'].forEach((id) =>
    document.getElementById(id)?.remove(),
  );
}

// Supprime la règle de démo (celle qui pointe vers la catégorie d'exemple).
// Les règles se persistent via la liste complète : on renvoie la liste sans la
// nôtre → le back la supprime.
async function deleteSeedRule(catId: string) {
  try {
    await store.dispatch('bank/actionGetRules', {});
    const rules = (store as any).state.bank?.rules || [];
    // Comme la suppression canonique (deleteRule) : on retire la règle ET on
    // re-séquence les priorités (sinon la liste est trouée → suppression bancale).
    const filtered = rules
      .filter((r: {category?: string}) => r.category !== catId)
      .map((r: object, i: number) => ({...r, priority: i + 1}));
    if (filtered.length !== rules.length) {
      await store.dispatch('bank/actionSetRules', {
        rules: filtered,
        trackId: 'tour-cleanup',
      });
    }
  } catch {
    /* best-effort */
  }
}

// ── Nettoyage de la catégorie d'exemple ──────────────────────────────────────
async function deleteSeedCategory() {
  cleanupSpans();
  let id: string | null = null;
  try {
    id = localStorage.getItem('fygr-tour-category');
  } catch {
    id = null;
  }
  if (!id) return;
  // D'abord la règle (elle référence la catégorie), puis la catégorie.
  await deleteSeedRule(id);
  try {
    // Catégorie racine sans sous-catégorie : suppression simple. targetCategoryId
    // laissé nul (rien à réaffecter pour une catégorie d'exemple sans flux).
    await store.dispatch('app/actionRemoveCategory', {
      categoryId: id,
      parentId: null,
      targetCategoryId: null,
    });
  } catch {
    /* best-effort : à durcir quand la partie transactions affectera des flux */
    return;
  }
  try {
    localStorage.removeItem('fygr-tour-category');
  } catch {
    /* ignore */
  }
}

// ── Transaction de démo (côté client, injectée par Transactions.vue) ──────────
const DEMO_TX_LABEL = 'VIREMENT BANQUE OKIMIA';

// Rectangle englobant les 3 transactions de démo (leurs libellés) : sert à
// spotlighter le groupe (sélection + résultat).
function demoTxGroupSelector(): string | null {
  const labels = Array.from(document.querySelectorAll('span, div, td')).filter(
    (n) => (n.textContent || '').trim() === DEMO_TX_LABEL,
  );
  if (!labels.length) return null;
  // Autorise la sélection de texte sur les 3 libellés démo malgré le garde-clic
  // du moteur (sinon mousedown bloqué → pas de sélection).
  labels.forEach((l) => l.classList.add('tour-allow-select'));
  const rects = labels.map((l) => l.getBoundingClientRect());
  const top = Math.min(...rects.map((r) => r.top));
  const bottom = Math.max(...rects.map((r) => r.bottom));
  // Largeur = toute la ligne (jusqu'à la colonne catégorie) via le conteneur du
  // tableau, pour qu'on voie la catégorie changer.
  const grid = document.querySelector('.grid-holder')?.getBoundingClientRect();
  const left = grid ? grid.left : Math.min(...rects.map((r) => r.left));
  const right = grid ? grid.right : Math.max(...rects.map((r) => r.right));
  const span =
    document.getElementById('tour-tx-group') || document.createElement('div');
  span.id = 'tour-tx-group';
  span.style.cssText = [
    'position:fixed',
    `top:${top}px`,
    `left:${left}px`,
    `width:${right - left}px`,
    `height:${bottom - top}px`,
    'pointer-events:none',
    'z-index:1',
  ].join(';');
  if (!span.parentNode) document.body.appendChild(span);
  return '#tour-tx-group';
}

// Cible de l'étape RÉSULTAT : ne renvoie JAMAIS null (le tour est
// interactive:false → une cible nulle ferait sauter le moteur à la fin →
// suppression prématurée). Span précis des 3 si présents, sinon le tableau.
function resultTargetSelector(): string | null {
  return (
    demoTxGroupSelector() ||
    (document.querySelector('.grid-holder') ? '.grid-holder' : null)
  );
}

// Item « Créer une règle » dans le menu de sélection (.fy-submenu).
function createRuleItemSelector(): string | null {
  const items = Array.from(document.querySelectorAll('.fy-submenu-item'));
  const match = items.find(
    (n) => (n.textContent || '').trim() === 'Créer une règle',
  ) as HTMLElement | undefined;
  if (!match) return null;
  match.setAttribute('data-tour-rule', 'create');
  return '[data-tour-rule="create"]';
}

// Cible le dropdown catégorie de LA RÈGLE EN COURS DE CRÉATION (pas des règles
// existantes affichées en cas de conflit). En conflit, elle est dans .new-rule ;
// sinon c'est la seule / la dernière .fy-rr-category.
function newRuleCategorySelector(): string | null {
  const scope = document.querySelector('.new-rule') || document;
  const cats = scope.querySelectorAll('.fy-rr-category');
  const el = cats[cats.length - 1] as HTMLElement | undefined;
  if (!el) return null;
  document
    .querySelectorAll('[data-tour-rulecat="1"]')
    .forEach((n) => n.removeAttribute('data-tour-rulecat'));
  el.setAttribute('data-tour-rulecat', '1');
  return '[data-tour-rulecat="1"]';
}

// Avance quand la catégorie choisie dans la règle en cours est « Catégorie Okimia ».
function categoryChosenSelector(): string | null {
  const cat =
    document.querySelector('[data-tour-rulecat="1"]') ||
    document.querySelector('.fy-rr-category');
  if (cat && (cat.textContent || '').includes(SEED_NAME)) {
    cat.setAttribute('data-tour-catset', '1');
    return '[data-tour-catset="1"]';
  }
  return null;
}

// Sortie : nettoyage complet — on retire la transaction de démo (client-only),
// puis on supprime la règle de démo ET la catégorie d'exemple (deleteSeedCategory
// enlève d'abord la règle qui la référence, puis la catégorie).
async function onTourExit() {
  document.body.classList.remove('tour-lock-cat-scroll');
  AppBus.$emit('tour:transaction-cleanup');
  await deleteSeedCategory();
}

window.FygrTour.register('categorization-overview', {
  title: 'Catégoriser vos transactions',
  // Le tuto démarre sur /categories (puis navigue vers /transactions dans son
  // déroulé). onStart pousse déjà /categories, mais `page` sert au lancement
  // depuis une autre route → on l'aligne pour éviter une nav /dashboard inutile.
  page: '/categories',
  interactive: false,
  intro: {
    title: 'La catégorisation',
    body: 'Catégoriser vos transactions, c’est donner du sens à vos flux. Nous allons créer une catégorie d’exemple, puis voir comment l’associer automatiquement à vos transactions.',
    note: 'Une catégorie d’exemple est utilisée pour la démonstration : elle est supprimée automatiquement à la fin.',
    startLabel: 'Commencer',
    optoutLabel: 'Désactiver les guides automatiques',
    optoutNote: 'Ils ne s’afficheront plus automatiquement. Vous pourrez les relancer à tout moment depuis « Tutoriels ».',
  },
  onStart: async () => {
    try {
      localStorage.removeItem('fygr-tour-category');
    } catch {
      /* ignore */
    }
    await router.push('/categories').catch(() => {});
  },
  onExit: onTourExit,
  steps: [
    {
      // Présentation de la page catégories. En avançant, on crée directement la
      // catégorie d'exemple (seed) pour gagner les 2 étapes de création manuelle.
      target: '.categories-container',
      title: 'Vos catégories',
      body: 'Ici se trouvent les catégories qui régissent le classement de vos transactions. Chacune porte un nom, un taux de TVA et un type.',
      placement: 'top',
      lockTarget: true,
      waitTimeout: 8000,
      onNext: async () => {
        if (!document.querySelector('[data-tour-row="cat-demo"]')) {
          AppBus.$emit('tour:category-seed', SEED_NAME);
        }
      },
    },
    {
      // La catégorie d'exemple, créée automatiquement — partie gauche.
      target: leftPartSelector,
      title: `« ${SEED_NAME} » a été créée pour l’exemple`,
      body: 'À gauche, modifiez à tout moment son nom, sa TVA appliquée et son type.',
      placement: 'top',
      lockTarget: true,
      waitTimeout: 12000,
    },
    {
      // Partie droite — même numéro d'étape que la gauche (groupWithPrevious).
      target: rightPartSelector,
      title: 'Les actions',
      body: 'Ici, vous pouvez exclure, dupliquer ou supprimer cette catégorie.',
      placement: 'top',
      lockTarget: true,
      waitTimeout: 5000,
      groupWithPrevious: true,
      // On file ensuite sur les transactions et on injecte une transaction de
      // démo (client-only) pour la suite du guide.
      onNext: async () => {
        document.body.classList.remove('tour-lock-cat-scroll');
        await router.push('/transactions').catch(() => {});
        for (let i = 0; i < 40 && !document.querySelector('.grid-holder'); i++) {
          await new Promise((r) => setTimeout(r, 80));
        }
        AppBus.$emit('tour:transaction-seed');
      },
    },
    {
      // Transaction de démo + invitation à sélectionner un mot du libellé : le
      // menu (Copier / Créer une règle) apparaît → on avance.
      target: demoTxGroupSelector,
      title: 'Des transactions à classer',
      body: 'Nous avons ajouté 3 transactions d’exemple au même libellé. Sur la transaction de votre choix, sélectionnez le mot « BANQUE » avec votre curseur : un menu va apparaître.',
      placement: 'bottom',
      advanceWhen: '.fy-submenu',
      waitTimeout: 8000,
    },
    {
      // Menu de sélection : on met en avant « Créer une règle ».
      target: createRuleItemSelector,
      title: 'Créer une règle',
      body: 'Cliquez sur « Créer une règle » pour catégoriser automatiquement toutes les transactions contenant ce mot.',
      placement: 'bottom',
      clickToAdvance: true,
      waitFor: '.picker-confirm',
      waitTimeout: 6000,
    },
    {
      // Ouvre le dropdown de catégorie de la règle EN COURS DE CRÉATION.
      target: newRuleCategorySelector,
      title: 'Choisissez la catégorie',
      body: `Cliquez ici pour affecter la règle à une catégorie.`,
      placement: 'bottom',
      clickToAdvance: true,
      // Le dropdown s'ouvre au clic sur l'élément interne .c-dropdown (pas la
      // racine) : on cible ce sous-élément pour que « Suivant » l'ouvre aussi.
      allowWithin: '.c-dropdown',
      waitFor: '.f-dropdown-overlay',
      waitTimeout: 6000,
    },
    {
      // On spotlight le dropdown ouvert. Cliquer dedans ne sort PAS du tour :
      // les clics sur .f-dropdown-overlay sont autorisés et court-circuitent le
      // check d'exit (tourEngine.js:413, avant le :417). advanceWhen fait avancer
      // dès que la catégorie est choisie (avant que la fermeture ne gêne).
      target: '.f-dropdown-overlay .wrapper',
      title: 'Sélectionnez votre catégorie',
      body: `Tapez « Okimia » dans la recherche pour retrouver « ${SEED_NAME} », puis sélectionnez-la.`,
      placement: 'left',
      advanceWhen: categoryChosenSelector,
      groupWithPrevious: true,
    },
    {
      // Valider la règle — même numéro d'étape (création de la règle).
      target: '.picker-confirm',
      title: 'Validez la règle',
      body: 'Cliquez sur « Valider » : toutes les transactions contenant ce mot seront classées dans votre catégorie.',
      placement: 'left',
      clickToAdvance: true,
      // « Suivant » doit cliquer le vrai « Valider » (créer la règle), pas juste
      // avancer : sinon il diverge du CTA et onNext tourne dans le vide.
      clickTargetOnNext: true,
      groupWithPrevious: true,
      // Une fois la règle validée (popup fermé), on affecte les 3 transactions de
      // démo à la catégorie créée pour montrer la catégorisation automatique.
      // NB : on ne re-dispatche PAS de clic sur .picker-confirm (c'est la cible de
      // l'étape → ça re-déclencherait onTargetClick → double avance → skip).
      onNext: async () => {
        for (let i = 0; i < 40 && document.querySelector('.picker-confirm'); i++) {
          await new Promise((r) => setTimeout(r, 80));
        }
        // La création de règle déclenche des rechargements qui effacent les mocks.
        // On lance la catégorisation : Transactions.vue les ré-injecte en continu
        // (interval) jusqu'au nettoyage → elles restent visibles jusqu'à Terminer.
        AppBus.$emit('tour:transaction-categorize');
        // IMPORTANT : le tour est interactive:false → si la cible de l'étape
        // résultat est introuvable, le moteur saute à la fin. On attend donc que
        // les 3 transactions soient réellement rendues (après le hardReloadTable)
        // AVANT d'avancer vers l'étape résultat.
        for (let i = 0; i < 60; i++) {
          const rendered = Array.from(
            document.querySelectorAll('span, div, td'),
          ).some((n) => (n.textContent || '').trim() === DEMO_TX_LABEL);
          if (rendered) break;
          await new Promise((r) => setTimeout(r, 150));
        }
      },
    },
    {
      // Résultat : les 3 transactions « BANQUE » sont maintenant classées.
      // Cible non-sautable (resultTargetSelector ne renvoie jamais null).
      target: resultTargetSelector,
      title: 'Classées automatiquement',
      body: 'Regardez la colonne Catégorie : les 3 transactions « BANQUE » viennent d’être classées dans « ' + SEED_NAME + ' » — sans les traiter une par une. C’est toute la puissance des règles.',
      placement: 'top',
      lockTarget: true,
      waitTimeout: 6000,
    },
  ],
  celebration: {
    title: 'Catégorie maîtrisée',
    body: 'Les éléments d’exemple (catégorie, transaction et règle) ont été supprimés.',
    // Partie /transactions à venir : pour l'instant on termine sur l'écran de
    // prise en main.
    drawer: true,
  },
});

export {};
